// src/components/DiaryModal.js
import React, { useState } from 'react';
import { Modal, Button, Spinner, Toast, ToastContainer, Alert } from 'react-bootstrap';

export default function DiaryModal({
  show,
  onHide,
  diaryEntries = [],
  onSendEmail,
  onDownloadCsv,
  onEmailCsv
}) {
  // Compute today ISO in London
  const todayStr = new Date().toLocaleDateString('en-CA', {
    timeZone: 'Europe/London'
  });

  // 1️⃣ Keep only today+future jobs
  const upcoming = diaryEntries.filter(e => {
    const d = new Date(e.dateTime).toLocaleDateString('en-CA', {
      timeZone: 'Europe/London'
    });
    return d >= todayStr;
  });

  // 2️⃣ Group them by category: TODAY, TOMORROW, REST OF WEEK (Start Mondays), NEXT WEEK, FUTURE
  const grouped = upcoming.reduce((acc, entry) => {
    const entryDate = new Date(entry.dateTime);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Calculate end of current week (Sunday)
    const endOfCurrentWeek = new Date(today);
    const daysUntilSunday = 7 - today.getDay(); // 0 = Sunday, so 7 - 0 = 7, but we want 0
    const adjustedDaysUntilSunday = daysUntilSunday === 7 ? 0 : daysUntilSunday;
    endOfCurrentWeek.setDate(endOfCurrentWeek.getDate() + adjustedDaysUntilSunday);
    
    // Calculate start of next week (Monday)
    const startOfNextWeek = new Date(endOfCurrentWeek);
    startOfNextWeek.setDate(startOfNextWeek.getDate() + 1);
    
    // Calculate end of next week (Sunday)
    const endOfNextWeek = new Date(startOfNextWeek);
    endOfNextWeek.setDate(endOfNextWeek.getDate() + 6);

    // Reset time to compare dates only
    const entryDateOnly = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const tomorrowOnly = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
    const endOfCurrentWeekOnly = new Date(endOfCurrentWeek.getFullYear(), endOfCurrentWeek.getMonth(), endOfCurrentWeek.getDate());
    const startOfNextWeekOnly = new Date(startOfNextWeek.getFullYear(), startOfNextWeek.getMonth(), startOfNextWeek.getDate());
    const endOfNextWeekOnly = new Date(endOfNextWeek.getFullYear(), endOfNextWeek.getMonth(), endOfNextWeek.getDate());

    let category;
    if (entryDateOnly.getTime() === todayOnly.getTime()) {
      category = 'TODAY';
    } else if (entryDateOnly.getTime() === tomorrowOnly.getTime()) {
      category = 'TOMORROW';
    } else if (entryDateOnly <= endOfCurrentWeekOnly) {
      category = 'REST OF WEEK';
    } else if (entryDateOnly >= startOfNextWeekOnly && entryDateOnly <= endOfNextWeekOnly) {
      category = 'NEXT WEEK';
    } else {
      category = 'FUTURE';
    }

    (acc[category] ||= []).push(entry);
    return acc;
  }, {});

  // Sort entries within each category by time
  Object.keys(grouped).forEach(category => {
    grouped[category].sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
  });

  // State
  const [hasSent, setHasSent]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [errorMsg, setErrorMsg]   = useState('');
  const [showError, setShowError] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // kickoff send
  const doSend = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      await onSendEmail();
      setHasSent(true);
    } catch (err) {
      console.error('Failed to send diary:', err);
      setErrorMsg('Failed to send diary. Please try again.');
      setShowError(true);
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  const handleSendClick = () => {
    if (hasSent) {
      setShowConfirm(true);
    } else {
      doSend();
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={() => {
          // clear confirm if hiding
          setShowConfirm(false);
          onHide();
        }}
        size="lg"
        fullscreen="sm-down"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>My Upcoming Jobs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Object.keys(grouped).length > 0 ? (
            Object.entries(grouped).map(([category, entries]) => {
              // Group entries by date for REST OF WEEK and FUTURE sections
              const shouldGroupByDate = category === 'REST OF WEEK' || category === 'FUTURE';
              
              if (shouldGroupByDate) {
                // Group entries by date
                const groupedByDate = entries.reduce((acc, entry) => {
                  const date = new Date(entry.dateTime).toLocaleDateString('en-GB', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    timeZone: 'Europe/London'
                  });
                  if (!acc[date]) acc[date] = [];
                  acc[date].push(entry);
                  return acc;
                }, {});

                return (
                  <div key={category} className="mb-4">
                    <h5 className="text-primary fw-bold">
                      {category === 'REST OF WEEK' ? 'REST OF WEEK (Start Mondays)' : category}
                    </h5>
                    {Object.entries(groupedByDate).map(([date, dateEntries]) => (
                      <div key={date} className="mb-3">
                        <h6 className="text-secondary fw-bold border-bottom pb-1">{date}</h6>
                        <div className="table-responsive">
                          <table className="table table-striped table-bordered">
                            <thead>
                              <tr>
                                <th>Time</th>
                                <th>Pickup → Drop-off</th>
                                <th>Customer</th>
                                <th>Phone</th>
                                <th>Passengers</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dateEntries.map(e => (
                                <tr key={e._id}>
                                  <td>
                                    {new Date(e.dateTime).toLocaleTimeString(
                                      'en-GB', {
                                        hour:    '2-digit',
                                        minute:  '2-digit',
                                        hour12:  true,
                                        timeZone:'Europe/London'
                                      }
                                    ).toLowerCase()}
                                  </td>
                                  <td>{e.from} → {e.to}</td>
                                  <td>{e.customerName}</td>
                                  <td>{e.customerPhone}</td>
                                  <td>{e.passengers}</td>
                                  <td>
                                    {e.status.charAt(0).toUpperCase() + e.status.slice(1)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              } else {
                // Regular display for TODAY, TOMORROW, NEXT WEEK
                return (
                  <div key={category} className="mb-4">
                    <h5 className="text-primary fw-bold">
                      {category === 'REST OF WEEK' ? 'REST OF WEEK (Start Mondays)' : category}
                    </h5>
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Pickup → Drop-off</th>
                            <th>Customer</th>
                            <th>Phone</th>
                            <th>Passengers</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.map(e => (
                            <tr key={e._id}>
                              <td>
                                {new Date(e.dateTime).toLocaleTimeString(
                                  'en-GB', {
                                    hour:    '2-digit',
                                    minute:  '2-digit',
                                    hour12:  true,
                                    timeZone:'Europe/London'
                                  }
                                ).toLowerCase()}
                              </td>
                              <td>{e.from} → {e.to}</td>
                              <td>{e.customerName}</td>
                              <td>{e.customerPhone}</td>
                              <td>{e.passengers}</td>
                              <td>
                                {e.status.charAt(0).toUpperCase() + e.status.slice(1)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              }
            })
          ) : (
            <p>No jobs scheduled from today onward.</p>
          )}
        </Modal.Body>

        {/* Inline confirmation prompt */}
        {showConfirm && (
          <Alert variant="warning" className="mx-3">
            You already sent today's diary. Do you want to send it again?
            <div className="mt-2 text-end">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setShowConfirm(false)}
                disabled={loading}
                className="me-2"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="warning"
                onClick={doSend}
                disabled={loading}
              >
                {loading
                  ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true"/>
                  : 'Yes, send again'}
              </Button>
            </div>
          </Alert>
        )}

        <Modal.Footer>
          {onDownloadCsv && (
            <Button
              variant="outline-primary"
              onClick={onDownloadCsv}
              disabled={loading || upcoming.length === 0}
            >
              📥 Download CSV
            </Button>
          )}
          {onEmailCsv && (
            <Button
              variant="outline-primary"
              onClick={onEmailCsv}
              disabled={loading || upcoming.length === 0}
            >
              📧 Email CSV
            </Button>
          )}
          <Button
            variant={hasSent ? 'outline-success' : 'success'}
            onClick={handleSendClick}
            disabled={loading || upcoming.length === 0}
          >
            {loading ? (
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true"/>
            ) : hasSent ? (
              'Sent (click to resend)'
            ) : (
              'Send Diary to Email'
            )}
          </Button>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error toast */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          onClose={() => setShowError(false)}
          show={showError}
          delay={4000}
          autohide
          bg="danger"
        >
          <Toast.Body className="text-white">{errorMsg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
}
